/**
 * Practice areas.
 *
 * A department is not only an organisational label: it is what the `department`
 * permission scope resolves through. Creating one and putting somebody in it is an
 * access-control act as much as an administrative one, which is why the create form
 * says so and why the capability is `organization.create` rather than something softer.
 *
 * There is no archive control here. The resource supports a soft delete, but a member
 * whose `department_id` points at an archived row is a scope question this screen
 * should not answer on its own — see the note in the build report.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  usePaginatedList,
  type Department,
} from '@legalcrm/api';
import {
  Button,
  Form,
  FormActions,
  FormInput,
  FormSelect,
  Modal,
  Table,
  useToast,
  useZodForm,
  type Column,
} from '@legalcrm/ui';
import { useState } from 'react';
import { z } from 'zod';

import { Can } from '@/auth/permissions';
import { formatDate } from '@/lib/format';

import { useFirmMembers } from './useFirmMembers';

const schema = z.object({
  name: z.string().min(1, 'Give the department a name'),
  parent: z.string(),
  head_user: z.string(),
});

type Values = z.infer<typeof schema>;

export function DepartmentsPage() {
  const [editing, setEditing] = useState<Department | null>(null);
  const [creating, setCreating] = useState(false);

  const list = usePaginatedList<Department>(qk.list('departments'), STAFF.departments, {
    limit: 100,
  });
  const members = useFirmMembers();

  const nameFor = (id: string | null) =>
    list.items.find((row) => row.id === id)?.name ?? '—';

  const columns: Column<Department>[] = [
    {
      key: 'name',
      header: 'Department',
      render: (row) => <span className="text-ink">{row.name}</span>,
    },
    {
      key: 'parent',
      header: 'Sits under',
      hideOnMobile: true,
      render: (row) => <span className="text-ink-muted">{nameFor(row.parent)}</span>,
    },
    {
      key: 'head_user',
      header: 'Head',
      hideOnMobile: true,
      render: (row) => <span className="text-ink-muted">{members.nameFor(row.head_user)}</span>,
    },
    {
      key: 'created_at',
      header: 'Created',
      hideOnMobile: true,
      align: 'right',
      render: (row) => <span className="tabular-nums">{formatDate(row.created_at)}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <Can module="organization" action="edit">
          <Button variant="ghost" size="sm" onClick={() => setEditing(row)}>
            Edit
          </Button>
        </Can>
      ),
    },
  ];

  return (
    <>
      <div className="mb-3 flex items-center justify-end">
        <Can module="organization" action="create">
          <Button variant="primary" onClick={() => setCreating(true)}>
            New department
          </Button>
        </Can>
      </div>

      <Table
        caption="Departments"
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        emptyTitle="No departments"
        emptyDescription="This firm has not divided itself into practice areas."
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
        }}
      />

      <DepartmentDialog
        open={creating}
        onClose={() => setCreating(false)}
        department={null}
        departments={list.items}
      />
      <DepartmentDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        department={editing}
        departments={list.items}
      />
    </>
  );
}

function DepartmentDialog({
  open,
  onClose,
  department,
  departments,
}: {
  open: boolean;
  onClose: () => void;
  department: Department | null;
  departments: Department[];
}) {
  const api = useApi();
  const { toast } = useToast();
  const members = useFirmMembers({ assignableOnly: true });

  const form = useZodForm(schema, {
    values: {
      name: department?.name ?? '',
      parent: department?.parent ?? '',
      head_user: department?.head_user ?? '',
    },
  });

  const save = useApiMutation<Department, Values>(
    {
      mutationFn: (values) => {
        const body = {
          name: values.name,
          // Absent, never "". Both are UUID fields, and an empty string is a 400.
          parent: values.parent || undefined,
          head_user: values.head_user || undefined,
        };
        return department
          ? api.patch<Department>(`${STAFF.department(department.id)}/`, body)
          : api.post<Department>(`${STAFF.departments}/`, body);
      },
      invalidates: [qk.all('departments')],
    },
    {
      onSuccess: (saved) => {
        toast({
          title: department ? 'Department updated' : 'Department created',
          description: saved.name,
          tone: 'success',
        });
        onClose();
      },
      onError: (error) => {
        toast({
          title: 'The change was refused',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The department could not be saved. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={department ? 'Edit department' : 'New department'}
      description="Putting somebody in a department is what the department permission scope resolves through, so this decides what they can reach."
    >
      <Form
        form={form}
        ariaLabel={department ? 'Edit department' : 'New department'}
        error={save.error}
        onSubmit={(values) => save.mutateAsync(values)}
      >
        <FormInput form={form} name="name" label="Name" autoFocus autoComplete="off" />
        <FormSelect
          form={form}
          name="parent"
          label="Sits under"
          options={[
            { value: '', label: 'Nothing — this is a top-level department' },
            ...departments
              .filter((row) => row.id !== department?.id)
              .map((row) => ({ value: row.id, label: row.name })),
          ]}
        />
        <FormSelect
          form={form}
          name="head_user"
          label="Head of department"
          options={[
            { value: '', label: 'Nobody yet' },
            ...members.members.map((member) => ({
              // The `User` id, not the membership id.
              value: member.user,
              label: member.full_name || member.email,
            })),
          ]}
        />
        <FormActions
          submitLabel={department ? 'Save department' : 'Create department'}
          busy={save.isPending}
          onCancel={onClose}
        />
      </Form>
    </Modal>
  );
}
